const cities = [
  { name: "Alexandria", zips: "71301, 71302, 71303" },
  { name: "Pineville", zips: "71360, 71361" },
  { name: "Ball", zips: "71405" },
  { name: "Woodworth", zips: "71485" },
  { name: "Natchitoches", zips: "71457" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required. Complete your order from Alexandria, Pineville, or anywhere in Central Louisiana.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Ship overnight from any Alexandria-area UPS or FedEx. No 200-mile round trip to New Orleans or Houston.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed before submission — critical for Fort Polk families and medical professionals on tight schedules.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment or long-distance drive needed.",
  },
];

export default function AlexandriaLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white pt-6 md:pt-8 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Alexandria &amp; Central Louisiana
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Alexandria, Louisiana sits at the geographic center of the state, spanning both banks of the Red River. It is the commercial, medical, and military hub of Central Louisiana — a region that extends north to Natchitoches, south toward the Atchafalaya Basin, east toward the Kisatchie National Forest, and west toward the Texas border. That geographic isolation is both a strength and a challenge when it comes to passport services: Alexandria residents are roughly 200 miles from New Orleans and 350 miles from Houston, the two nearest regional passport agencies.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Fort Polk, now known as Fort Johnson, is located just 25 miles southwest of Alexandria in Leesville. The installation hosts the Joint Readiness Training Center (JRTC) — one of the Army's premier combat training centers — and regularly cycles thousands of soldiers through rotational training. The permanent party population and their families represent one of the most time-sensitive passport markets in Louisiana: PCS orders to overseas assignments in Germany, Korea, Japan, and Italy arrive with compressed timelines, and every family member's passport must be current before departure.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Alexandria's medical community creates its own passport demand. Rapides Regional Medical Center and Christus St. Frances Cabrini Hospital are the two dominant healthcare systems serving Central Louisiana. Physicians, surgeons, and specialists travel to international medical conferences in Vienna, London, Tokyo, and Toronto. Healthcare administrators participate in international accreditation visits. Medical students and residents at LSU Health Sciences Center rotate through international programs. These travelers need reliable, predictable passport processing — not a 400-mile round trip on a weekday.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              Louisiana State University of Alexandria (LSUA) and Louisiana College in Pineville send students on study-abroad programs throughout Europe and Latin America. Agribusiness operators in the Red River Agricultural Corridor travel to South America for commodity trade meetings and to Southeast Asia for agricultural technology exchanges. Fast Passport Center serves the entire Central Louisiana corridor: start online from anywhere in the region, ship your documents overnight, and we handle everything from there.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Alexandria, LA</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Rapides Parish &amp; Central Louisiana</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — start online, ship overnight, we handle the rest
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">No New Orleans or Houston trip required</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  Start online, then <strong className="text-white">ship with our prepaid label</strong> from any Alexandria-area UPS or FedEx. Most Fort Polk families and Central Louisiana clients ship overnight — no 200-mile drive required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Central Louisiana — start online, ship overnight, and we handle the rest. No need to drive 200 miles to New Orleans or 350 miles to Houston.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Shreveport", href: "/shreveport" },
                    { name: "Monroe", href: "/monroe" },
                    { name: "Baton Rouge", href: "/baton-rouge" },
                    { name: "Lafayette", href: "/lafayette" },
                  ].map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Central Louisiana Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}